<template>
    <BaseCard padding="none" class="flex flex-col w-full mt-5">
        <div class="flex flex-col gap-6 md:p-10 p-5 h-full justify-start">
            <div class="flex flex-col items-start gap-6">
                <h3 class="title-subsection text-black">{{ feedbackQuestion.question }}</h3>
                <!-- Overall Rating -->
                <div class="flex flex-col gap-2.5 justify-center items-start">
                    <div class="body-small text-black">{{ t("feedback.summary.overallRating") }}</div>
                    <div
                        v-if="feedbackSummaryResponse.rating.score"
                        class="flex flex-row gap-1 relative"
                        role="img"
                        :aria-label="t('feedback.summary.ratingAriaLabel', { question: feedbackQuestion.question, score: feedbackSummaryResponse.rating.score })">
                        <i v-for="n in 5" :key="n" class="bi bi-star-fill text-border px-1" aria-hidden="true"></i>
                        <div class="flex flex-row gap-1 overflow-x-hidden absolute" :style="{ width: `${feedbackSummaryResponse.rating.score * 20}%` }">
                            <i v-for="n in 5" :key="n" class="bi bi-star-fill text-ink px-1" aria-hidden="true"></i>
                        </div>
                    </div>
                    <div v-else class="body-small text-black">{{ t("feedback.summary.notEnoughData") }}</div>
                </div>
            </div>
            <!-- Overview -->
            <div class="flex flex-col gap-1">
                <div class="body-small-semibold text-black">{{ t("feedback.summary.overviewHeading") }}</div>
                <div class="body-small text-black">
                    {{ feedbackSummaryResponse.concludingReflection || t("feedback.summary.overviewEmpty") }}
                </div>
            </div>
            <!-- Strength / Growth -->
            <div class="flex flex-col md:flex-row gap-5 justify-start items-start text-black">
                <div class="flex flex-col gap-1 w-full">
                    <div class="text-black body-small-semibold">{{ t("feedback.summary.strengthsHeading") }}</div>
                    <template v-if="feedbackSummaryResponse.strengths.length > 0">
                        <ul class="m-0 pl-5 pr-2">
                            <li v-for="(strength, idx) in feedbackSummaryResponse.strengths" :key="idx" class="m-0! body-small">
                                {{ strength }}
                            </li>
                        </ul>
                    </template>
                    <template v-else>
                        <div class="px-3 pt-2 pl-6 body-small text-black">{{ t("feedback.summary.notEnoughData") }}</div>
                    </template>
                </div>
                <div class="flex flex-col gap-1 text-black w-full">
                    <div class="body-small-semibold text-black w-full">{{ t("feedback.summary.growthAreasHeading") }}</div>
                    <template v-if="feedbackSummaryResponse.growthAreas.length > 0">
                        <ul class="m-0 pl-5 pr-2">
                            <li v-for="(growth, idx) in feedbackSummaryResponse.growthAreas" :key="idx" class="m-0! body-small">
                                {{ growth }}
                            </li>
                        </ul>
                    </template>
                    <template v-else>
                        <div class="px-3 pt-2 pl-6 text-black body-small">{{ t("feedback.summary.notEnoughData") }}</div>
                    </template>
                </div>
            </div>
            <!-- Examples -->
            <div v-if="feedbackSummaryResponse.examples.length > 0" class="title-body flex flex-col gap-1 text-ink-muted">
                <div class="flex flex-col gap-1 text-black">
                    <div class="body-small-semibold text-black">{{ t("feedback.summary.examplesHeading") }}</div>
                    <template v-if="feedbackSummaryResponse.examples.length > 0">
                        <ul class="m-0 pl-5 pr-2">
                            <li v-for="(example, idx) in feedbackSummaryResponse.examples" :key="idx" class="m-0! body-small">
                                {{ example }}
                            </li>
                        </ul>
                    </template>
                    <template v-else>
                        <div class="px-3 pt-2 pl-6 text-black body-small">{{ t("feedback.summary.notEnoughData") }}</div>
                    </template>
                </div>
            </div>
            <div class="flex flex-col gap-5 w-75 mx-auto justify-end items-center h-full pt-6">
                <BaseButton theme="primary" class="w-full" @click="sendFeedbackResponseSummary()"> {{ t("feedback.summary.sendButton") }} </BaseButton>
                <BaseButton theme="tertiary" class="w-full" @click="showChatMessageControls()">{{ t("feedback.summary.reviseButton") }}</BaseButton>
            </div>
        </div>
    </BaseCard>
</template>

<script setup>
import { getCookie } from "/js/utils";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { useFeedbackChatStore } from "~vue/stores/feedbackChatStore";
import { logError } from "~vue/utils/logUtils";
import { computed, inject, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
    feedbackSummary: {
        type: Object,
        required: true,
    },
});

const feedbackSummaryResponse = computed(() => {
    return processFeedbackMatch(props.feedbackSummary);
});

const feedbackQuestion = inject("feedbackQuestion");
const coachingSessionId = inject("coachingSessionId");

const showChatMessageControls = () => {
    useFeedbackChatStore.setShowChatMessageControls(true);
};

const processFeedbackMatch = (feedbackMatch) => {
    // Trim the initial content to handle any leading/trailing whitespace
    const content = typeof feedbackMatch === "string" ? feedbackMatch.trim() : feedbackMatch;

    // Function to recursively parse XML tags and flatten nested ones
    const parseAllXmlTags = (text, parentTag = null) => {
        const xmlPattern = /<(\w+)>\s*([\s\S]*?)\s*<\/\1>/g;
        const tags = [];
        let match;

        xmlPattern.lastIndex = 0;

        while ((match = xmlPattern.exec(text)) !== null) {
            const tagName = match[1];
            const tagContent = match[2].trim();

            // Skip empty tags
            if (!tagContent) {
                continue;
            }

            // Check if this content has nested XML tags
            const hasNestedTags = /<\w+>[\s\S]*?<\/\w+>/.test(tagContent);

            if (hasNestedTags) {
                // Parse nested tags and add them with parent context
                const nestedTags = parseAllXmlTags(tagContent, tagName);
                tags.push(...nestedTags);

                // Also keep the parent tag with cleaned content (text only)
                const cleanContent = tagContent.replace(/<\w+>[\s\S]*?<\/\w+>/g, "").trim();

                if (cleanContent) {
                    tags.push({
                        tag: tagName,
                        content: cleanContent,
                        hasChildren: true,
                    });
                }
            } else {
                // No nested tags, add as-is (only if content exists)
                tags.push({
                    tag: parentTag ? `${parentTag}_${tagName}` : tagName,
                    content: tagContent,
                    parent: parentTag,
                });
            }
        }

        return tags;
    };

    const allTags = parseAllXmlTags(content);

    // Organize into structured object
    const data = {
        rating: {
            score: null,
            scaleDefinition: null,
            rationale: null,
        },
        strengths: [],
        growthAreas: [],
        examples: [],
        concludingReflection: null,
    };

    allTags.forEach((tag) => {
        // Skip empty content
        if (!tag.content || tag.content.trim() === "") {
            return;
        }

        // Handle both direct tags and nested tags
        const tagName = tag.tag.toLowerCase();

        switch (true) {
            case tagName === "rating_score" || tagName.includes("rating_score"):
                data.rating.score = tag.content.trim();
                break;
            case tagName === "rating_scale_definition" || tagName.includes("rating_scale_definition"):
                data.rating.scaleDefinition = tag.content.trim();
                break;
            case tagName === "rating_rationale" || tagName.includes("rating_rationale"):
                data.rating.rationale = tag.content.trim();
                break;
            case tagName === "concluding_reflection" || tagName.includes("concluding_reflection"):
                data.concludingReflection = tag.content.trim();
                break;
            case tagName.includes("strength"):
                // Only push if content is not empty AND not already in array
                if (tag.content.trim() && !data.strengths.includes(tag.content.trim())) {
                    data.strengths.push(tag.content.trim());
                }
                break;
            case tagName.includes("growth_area") || tagName.includes("growth"):
                // Only push if content is not empty AND not already in array
                if (tag.content.trim() && !data.growthAreas.includes(tag.content.trim())) {
                    data.growthAreas.push(tag.content.trim());
                }
                break;
            case tagName.includes("example"):
                // Only push if content is not empty AND not already in array
                if (tag.content.trim() && !data.examples.includes(tag.content.trim())) {
                    data.examples.push(tag.content.trim());
                }
                break;
        }
    });

    return data;
};

const sendFeedbackResponseSummary = async () => {
    try {
        const response = await fetch("/api/accounts/feedback-response", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
                question_id: feedbackQuestion.hashid,
                content: feedbackSummaryResponse.value,
                coaching_session: coachingSessionId,
            }),
        });

        if (!response.ok) {
            logError("Error in sending feedback response summary", response.body);
            throw new Error("Error sending feedback response summary");
        }
    } catch (error) {
        console.error("Error in sending feedback response summary:", error);
        throw error;
    }

    useFeedbackChatStore.setIsOutro(true);
    useFeedbackChatStore.setShowLandingPage(true);
};

onMounted(() => {
    // Emit an event to indicate that the component has finished rendering
    useFeedbackChatStore.setShowChatMessageControls(false);
});
</script>

<style scoped></style>
